import { useState } from 'react'

import { Form, Formik } from 'formik'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import * as Yup from 'yup'

import {
  type OrchestratedOfferingListingDetails,
  type OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import { usePrecisionNumberFormat } from '@b-stock/fe-core/bstock-react'

import { GTM_PLATFORM } from '@/helpers/telemetry/constants'
import { orchestratedListingToGTMItemInput } from '@/helpers/telemetry/gtmItemAdapters'
import { sendTypedGTMEvent } from '@/helpers/telemetry/sendTypedGTMEvent'
import type { GTMMaoIntentEvent } from '@/helpers/types/gtmTypes'

import { getOfferAmount } from './getOfferAmount'
import { MakeAnOfferConfirmationModal } from './MakeAnOfferConfirmationModal'

export type MakeAnOfferFormValues = {
  amount: number | null
  minOfferAmount: number
  currentOfferAmount: number
}

const FullWidthForm = styled(Form)`
  width: 100%;
`

export const MakeAnOfferFormProvider = ({
  children,
  listing,
  selectedQuote,
}: {
  children: React.ReactNode
  listing: OrchestratedOfferingListingDetails
  selectedQuote?: OrchestratedQuote | null
}) => {
  const intl = useIntl()
  const { offering } = listing
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const currentOfferAmount = getOfferAmount({ listing })

  const currency = listing.currency ?? 'USD'

  const { formatPrecisionNumber } = usePrecisionNumberFormat({
    precision: offering?.bidPrecision ?? 1,
    format: listing.contract?.bidBasis === 'RELATIVE' ? 'percent' : 'currency',
    currency,
  })

  if (!offering) {
    return null
  }

  const minOfferAmount =
    offering.startOfferAmount ||
    (listing.listingType === 'SPOT' ? 10000 : offering.bidPrecision)

  const minOfferAmountError = intl.formatMessage(
    {
      id: 'Listing.actionColumn.simpleOfferActionModule.minOfferAmountError',
    },
    { minOfferAmount: formatPrecisionNumber(minOfferAmount) }
  )
  const requiredError = intl.formatMessage(
    {
      id: 'Common.error.required',
    },
    {
      fieldName: 'Offer amount',
    }
  )

  return (
    <Formik
      initialValues={{
        amount: undefined,
        minOfferAmount,
        currentOfferAmount,
      }}
      validateOnBlur
      onSubmit={() => {
        const itemInput = orchestratedListingToGTMItemInput(listing)
        const maoIntentEvent: GTMMaoIntentEvent = {
          event: 'mao_intent',
          platform: GTM_PLATFORM,
          item_id: listing._id,
          ecommerce: {
            currency,
            items: [{ ...itemInput, index: 0, quantity: 1 }],
          },
        }
        sendTypedGTMEvent(maoIntentEvent)
        setShowConfirmationModal(true)
      }}
      validationSchema={Yup.object({
        amount: Yup.number()
          .required(requiredError)
          .min(minOfferAmount, minOfferAmountError),
        minOfferAmount: Yup.number().required(),
        currentOfferAmount: Yup.number().required(),
      })}
      enableReinitialize
    >
      {({ handleSubmit }) => {
        const onSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
          e.preventDefault()
          handleSubmit()
        }
        return (
          <FullWidthForm onSubmit={onSubmit}>
            {children}
            {showConfirmationModal && (
              <MakeAnOfferConfirmationModal
                closeModal={() => setShowConfirmationModal(false)}
                listing={listing}
                offerId={listing.offers?.[listing.offers.length - 1]?._id}
                selectedQuote={selectedQuote}
              />
            )}
          </FullWidthForm>
        )
      }}
    </Formik>
  )
}
